/**
 * Process items in batches with controlled concurrency
 * @param {Array} items - items to process
 * @param {Function} processor - async function to apply to each item
 * @param {number} batchSize - max concurrent operations (default 5)
 * @returns {Promise<Array>} Results in same order as input (errors return original item)
 */
export async function processBatch(items, processor, batchSize = 5) {
  if (items.length === 0) return [];

  const results = new Array(items.length);

  for (let i = 0; i < items.length; i += batchSize) {
    const startIndex = i;
    const batch = items.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map((item) => processor(item)));
    settled.forEach((result, idx) => {
      results[startIndex + idx] = result.status === "fulfilled" ? result.value : batch[idx];
    });
  }

  return results;
}

/**
 * Process items with progress callback
 * @param {Array} items - items to process
 * @param {Function} processor - async function to apply to each item
 * @param {Function} onProgress - callback(completed, total) for progress updates
 * @param {number} batchSize - max concurrent operations (default 5)
 * @returns {Promise<Array>} Results in same order as input (errors return original item)
 */
export async function processBatchWithProgress(items, processor, onProgress, batchSize = 5) {
  if (items.length === 0) return [];

  const results = new Array(items.length);
  let completed = 0;
  const total = items.length;

  for (let i = 0; i < items.length; i += batchSize) {
    const startIndex = i;
    const batch = items.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map((item) => processor(item)));
    settled.forEach((result, idx) => {
      results[startIndex + idx] = result.status === "fulfilled" ? result.value : batch[idx];
    });
    completed += batch.length;
    onProgress?.(Math.min(completed, total), total);
  }

  return results;
}
